import React, { useState, useEffect } from 'react';
import { PMT2Product } from '@/types/pmt2Types';
import { pmt2Service, ExperimentResultsData } from '@/services/pmt2Service';
import { Link as LinkIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';

interface ExperimentResultsDashboardProps {
  product: PMT2Product | null;
}

const ExperimentResultsDashboard: React.FC<ExperimentResultsDashboardProps> = ({ product }) => {
  const [data, setData] = useState<ExperimentResultsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!product) {
        setData(null);
        setError(null);
        return setLoading(false);
      }
      try {
        setLoading(true);
        const result = await pmt2Service.getExperimentResultsData(product.companyName, product.id);
        setData(result);
        setError(null);
      } catch (err) {
        console.error('[ExperimentResultsDashboard] Error:', err);
        setError('Failed to load experiment results.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [product]);

  const formatDate = (date: Date | null) =>
    date ? date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' }) : '-';

  const formatDollar = (value: number | null) =>
    value !== null && !isNaN(value) ? `$${Math.round(value)}` : '-';

  const formatPercent = (value: number | null) =>
    value !== null && !isNaN(value) ? `${Math.round(value)}%` : '-';

  if (!product) return null;

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Latest Experiment (GP Only)</CardTitle>
        </CardHeader>
        <CardContent className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
          <span className="ml-2">Loading experiment results...</span>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Latest Experiment (GP Only)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-md text-red-700 dark:text-red-300">
            {error || 'No data found.'}
          </div>
        </CardContent>
      </Card>
    );
  }

  const {
    finalPeriod1Start,
    finalPeriod1End,
    finalPeriod2Start,
    finalPeriod2End,
    grossProfitPeriod1,
    grossProfitPeriod2,
    note,
    generatorUrl,
  } = data;

  const numChange =
    grossProfitPeriod1 !== null && grossProfitPeriod2 !== null
      ? grossProfitPeriod2 - grossProfitPeriod1
      : null;

  const percentChange =
    grossProfitPeriod1 && grossProfitPeriod1 !== 0 && numChange !== null
      ? (numChange / grossProfitPeriod1) * 100
      : null;

  const days =
    finalPeriod2Start && finalPeriod2End
      ? Math.round((finalPeriod2End.getTime() - finalPeriod2Start.getTime()) / (1000 * 60 * 60 * 24)) + 1
      : null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          Latest Experiment (GP Only)
          {generatorUrl && (
            <a href={generatorUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
              <LinkIcon size={18} />
            </a>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-300 dark:border-gray-700">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-800">
                <th className="px-2 py-1 border text-left">Item</th>
                <th className="px-2 py-1 border text-left">Start</th>
                <th className="px-2 py-1 border text-left">End</th>
                <th className="px-2 py-1 border text-left">Gross Profit</th>
                <th className="px-2 py-1 border text-left"># Change</th>
                <th className="px-2 py-1 border text-left">% Change</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-2 py-1 border">Period 1</td>
                <td className="px-2 py-1 border">{formatDate(finalPeriod1Start)}</td>
                <td className="px-2 py-1 border">{formatDate(finalPeriod1End)}</td>
                <td className="px-2 py-1 border">{formatDollar(grossProfitPeriod1)}</td>
                <td className="px-2 py-1 border"></td>
                <td className="px-2 py-1 border"></td>
              </tr>
              <tr>
                <td className="px-2 py-1 border">Period 2</td>
                <td className="px-2 py-1 border">{formatDate(finalPeriod2Start)}</td>
                <td className="px-2 py-1 border">{formatDate(finalPeriod2End)}</td>
                <td className="px-2 py-1 border">{formatDollar(grossProfitPeriod2)}</td>
                <td className="px-2 py-1 border">{formatDollar(numChange)}</td>
                <td className="px-2 py-1 border">{formatPercent(percentChange)}</td>
              </tr>
              <tr>
                <td className="px-2 py-1 border">Days</td>
                <td className="px-2 py-1 border" colSpan={5}>
                  {days !== null ? days : '-'}
                </td>
              </tr>
              <tr>
                <td className="px-2 py-1 border">Note</td>
                <td className="px-2 py-1 border" colSpan={5}>
                  {note || '-'}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default ExperimentResultsDashboard;